import { getCurrentScope, ref, unref, watch } from 'vue'
import axiosInstance from '@/axios'
import { hasCatalogIntegration } from '@/utils/supplier-entitlement'
import {
    SUPPLIER_THROTTLED,
    normalizeRetryAfter,
    throttleMessage,
} from '@/utils/supplierThrottle'

// Mirrors the backend SKU regex enforced in pebble 2's supplier proxy
// (^[A-Za-z0-9\-]{2,32}$). Frontend defense-in-depth against poisoned
// legacy serverState values flowing into the URL path segment.
const SKU_RE = /^[A-Za-z0-9-]{2,32}$/

/**
 * Back-compat default for a caller that passes no supplier source.
 *
 * SanMar rather than "nothing", matching ``useSupplierCatalog``: an
 * un-migrated caller keeps its old behavior instead of silently fetching
 * nothing, which would be a regression that no test failure announces.
 */
const DEFAULT_SUPPLIER_KEY = 'sanmar'

/** The product-detail endpoint for one supplier key. */
function productEndpoint(supplierKey, sku) {
  return `/api/supplier/${encodeURIComponent(supplierKey)}/product/${encodeURIComponent(sku)}`
}

/**
 * useSanmarProductMeta — composable for fetching SanMar product metadata
 * by SKU from the backend supplier proxy (`GET /api/supplier/sanmar/product/<sku>`).
 *
 * Boulders card "Product editor (hi-fi redesign)", pebble 4 — Track 2.
 *
 * Public surface (locked contract):
 *
 *   const { meta, loading, error, fetchMeta } = useSanmarProductMeta()
 *
 *   meta:    ref<Object|null>      — last successful payload (sku, name,
 *                                    brand, base_cost, colors[], sizes[],
 *                                    fabric_weight, description, image_urls[]).
 *                                    Per-color shape:
 *                                      { color_sku, name?, hex, swatch_url }
 *                                    where `hex` and `swatch_url` may each be
 *                                    null independently. `swatch_url` (added
 *                                    by backend PR #1127) is the SanMar CDN
 *                                    fabric-swatch image URL when the
 *                                    `vendor_color_hex` table has a backfill
 *                                    entry; the ColorsPanel renders it as
 *                                    the chip background in preference to
 *                                    `hex`.
 *   loading: ref<boolean>          — request in flight
 *   error:   ref<{status,message}|null>
 *                                    A 409 `ambiguous_style` additionally
 *                                    carries `ambiguousStyle: true` and
 *                                    `candidates: [{style_id, brand, title,
 *                                    name}]` (Bug Intake #451).
 *   fetchMeta(sku, opts?): Promise<void>
 *                                  — populates `meta` or sets `error`.
 *                                    `opts.styleId` sends the product's
 *                                    persisted supplier style id.
 *                                    `opts.refresh === true` appends
 *                                    `?refresh=1` so the backend bypasses
 *                                    its catalog-detail Redis cache and
 *                                    recomposes from the supplier feed
 *                                    (backs the manual "Look up" button;
 *                                    card https://trello.com/c/D1Hn2K0u).
 *
 * Behavior notes:
 *   - Concurrent fetches are de-duped via AbortController on the prior
 *     request and a monotonic generation counter on returned responses.
 *     Late stale responses are silently discarded.
 *   - `meta` is NOT cleared synchronously on a new fetch — the prior
 *     payload stays visible so the UI doesn't flicker to an empty grid
 *     while the next request resolves.
 *   - `error` IS cleared at the start of each fetch.
 */
export function useSanmarProductMeta(
    supplierSource = DEFAULT_SUPPLIER_KEY,
    supplierNameSource = null,
) {
    const meta = ref(null)
    const loading = ref(false)
    const error = ref(null)

    // Tracks the most recent fetch so late responses can be discarded.
    let generation = 0
    let currentAbort = null
    // The supplier key `meta` was looked up under (null while `meta` is null).
    let metaSupplierKey = null
    // The supplier key of the request currently in flight (null when idle).
    let inflightSupplierKey = null

    /**
     * The supplier key to ask RIGHT NOW -- re-read per call, never
     * captured at construction.
     *
     * The picker can change between the mount-time pre-fill and a later
     * "Look up" click. A key resolved once in the closure would send the
     * second request to the first supplier, which is a subtler form of the
     * same wrong-supplier bug.
     */
    function currentSupplierKey() {
        const raw =
            typeof supplierSource === 'function'
                ? supplierSource()
                : unref(supplierSource)
        return (raw ?? '').toString().trim().toLowerCase()
    }

    /** The supplier's display name, for user-facing error copy. */
    function currentSupplierName() {
        const raw =
            typeof supplierNameSource === 'function'
                ? supplierNameSource()
                : unref(supplierNameSource)
        const name = (raw ?? '').toString().trim()
        return name || null
    }

    // A resolved product belongs to the supplier it was looked up under. When
    // the selected supplier moves, that lookup (and any still in flight for
    // the old supplier) is void: without this the card kept the OLD
    // supplier's SKU, brand, cost and description under the new supplier's
    // name until a reload (Bug Intake #324 / GH #4424). `meta` is otherwise
    // deliberately kept across a NEW fetch so the grid does not flicker; this
    // only fires when the supplier itself changes.
    //
    // THE WATCH IS ARMED LAZILY, on the first fetch, never at construction.
    // Callers pass the supplier as a getter over a ref they declare LATER in
    // their own setup (SupplierSkuPanel: `() => supplier.value`, "not called
    // until the first search"). A watch evaluates its getter immediately, so
    // arming it here would read that ref in its temporal dead zone and throw
    // at mount. It is armed inside the scope captured at construction so it
    // still dies with the component.
    const ownerScope = getCurrentScope()
    let watchArmed = false
    function onSupplierChanged(key) {
        // Old supplier's RESULT: void it. Never touches a request already
        // running for the new key (a swap stages the supplier and fetches in
        // the same tick), nor its loading flag.
        if (meta.value != null && key !== metaSupplierKey) {
            meta.value = null
            metaSupplierKey = null
        }
        // Old supplier's in-flight REQUEST: abort it and discard its answer.
        if (currentAbort != null && inflightSupplierKey !== key) {
            currentAbort.abort()
            currentAbort = null
            generation++
            inflightSupplierKey = null
            error.value = null
            loading.value = false
        }
    }
    function armSupplierWatch() {
        if (watchArmed) return
        watchArmed = true
        const arm = () => watch(currentSupplierKey, onSupplierChanged)
        if (ownerScope && ownerScope.active) ownerScope.run(arm)
        else if (!ownerScope) arm()
    }

    const fetchMeta = async (sku, opts = {}) => {
        armSupplierWatch()
        // No catalog behind this supplier -> no request, and no error.
        //
        // THIS IS THE LOAD-BEARING GUARD. Without it an S&S or local-vendor
        // SKU was sent to SanMar's endpoint, 404'd, and rendered
        // "Unknown SanMar SKU" to someone who never picked SanMar --
        // directly above a notice correctly saying their supplier has no
        // catalog integration. Two banners, one of them naming the wrong
        // company.
        //
        // Resolving to a QUIET state (not an error) is the point: an error
        // here would keep an orange banner up with different words, which
        // is the same contradiction wearing a new coat. The panel's
        // "showing saved values" notice is the correct and sufficient
        // message for this state.
        //
        // It also covers the pre-load window. The panel defaults its
        // supplier ref before the vendor list lands, so an unresolved
        // supplier must not be read as permission to fetch --
        // ``hasCatalogIntegration`` fails closed on null/empty for exactly
        // this call site.
        const supplierKey = currentSupplierKey()
        if (!hasCatalogIntegration(supplierKey)) {
            error.value = null
            meta.value = null
            loading.value = false
            // Cancel anything in flight from a previously-integrated
            // supplier, and bump the generation so its late response is
            // discarded rather than landing under the new selection.
            if (currentAbort) {
                currentAbort.abort()
                currentAbort = null
            }
            generation++
            return
        }

        // Defense-in-depth: validate SKU before URL interpolation. The
        // backend enforces this regex (pebble 2) and the autocomplete
        // emits objects (not free text), but the onMounted pre-fill path
        // reads product_item_number directly from serverState — a poisoned
        // legacy value containing `/`, `?`, `#`, or `..` would change the
        // URL semantics. Reject early with a friendly error.
        if (typeof sku !== 'string' || !SKU_RE.test(sku)) {
            error.value = { status: 0, message: 'Invalid SKU format', sku }
            meta.value = null
            loading.value = false
            return
        }

        // Cancel the prior request, if any.
        if (currentAbort) {
            currentAbort.abort()
        }

        // The previous result belongs to another supplier: do not show it
        // under this one while this lookup runs.
        if (meta.value != null && metaSupplierKey !== supplierKey) {
            meta.value = null
            metaSupplierKey = null
        }

        const myGeneration = ++generation
        inflightSupplierKey = supplierKey
        const controller =
            typeof AbortController !== 'undefined'
                ? new AbortController()
                : null
        currentAbort = controller

        loading.value = true
        error.value = null

        // `refresh` forces the backend to bypass its catalog-detail cache
        // and recompose from the supplier feed. The manual "Look up"
        // button sets this; the auto-fetch-on-SKU-change path leaves it
        // off so a warm cache serves the common case.
        //
        // `styleId` is the product's persisted supplier style id (S&S
        // styleID). An S&S style NAME is shared across brands ("5200" is
        // C2 Sport, Gildan and Bayside), so without it the backend
        // resolves the name to one of them.
        const params = []
        if (opts.refresh) params.push('refresh=1')
        if (opts.styleId != null) {
            params.push(`style_id=${encodeURIComponent(opts.styleId)}`)
        }
        const query = params.length ? `?${params.join('&')}` : ''
        try {
            const response = await axiosInstance.get(
                `${productEndpoint(supplierKey, sku)}${query}`,
                controller ? { signal: controller.signal } : undefined
            )

            // Discard if a newer fetch superseded this one.
            if (myGeneration !== generation) return

            meta.value = response.data
            metaSupplierKey = supplierKey
            error.value = null
        } catch (err) {
            // Discard stale errors too.
            if (myGeneration !== generation) return

            // Aborted requests should not surface as user-visible errors —
            // the superseding fetch owns the user feedback.
            const isAbort =
                err?.name === 'CanceledError' ||
                err?.name === 'AbortError' ||
                err?.code === 'ERR_CANCELED'
            if (isAbort) return

            const status = err?.response?.status ?? 0
            // `sku` names the lookup the error belongs to (swap clean-up guard).
            error.value = { ...mapError(status, currentSupplierName()), sku }
            // 409 `ambiguous_style`: the name is shared by several styles and
            // the product pins none. The candidates let the panel ask which
            // (Bug Intake #451); a 409 without them stays a plain failure.
            // 429 `supplier_throttled`: S&S's account-wide rate limit (shared
            // by every sandbox and dev). Not an outage and not a credential
            // problem -- waiting fixes it -- so it is flagged for the panel to
            // explain, and stays retryable (the next fetch clears it). A 429
            // without that code is NOT this condition and keeps the generic
            // failure copy.
            if (
                status === 429
                && err?.response?.data?.error === SUPPLIER_THROTTLED
            ) {
                const retryAfterSeconds = normalizeRetryAfter(
                    err.response.data.retry_after_seconds,
                )
                error.value = {
                    status: 429,
                    throttled: true,
                    retryAfterSeconds,
                    message: throttleMessage(retryAfterSeconds),
                    sku,
                }
            }
            const candidates = err?.response?.data?.candidates
            if (
                status === 409
                && err?.response?.data?.error === 'ambiguous_style'
                && Array.isArray(candidates)
                && candidates.length > 0
            ) {
                error.value = {
                    status: 409,
                    ambiguousStyle: true,
                    candidates,
                    message:
                        `More than one ${currentSupplierName() || 'supplier'} `
                        + `style is named ${sku}. Pick the one you mean.`,
                    sku,
                }
            }
            meta.value = null
        } finally {
            if (myGeneration === generation) {
                loading.value = false
                currentAbort = null
                inflightSupplierKey = null
            }
        }
    }

    return { meta, loading, error, fetchMeta }
}

/**
 * Map an HTTP status into the user-facing error shape.
 *
 * Distinct messages for 404 (user-correctable: pick another SKU), 500 (our
 * conversion of a successful supplier response failed -- a local bug, not
 * an upstream outage; retrying will not help), 502 (upstream supplier
 * problem: try again later), vs generic (network / unexpected).
 *
 * EVERY MESSAGE NAMES THE SUPPLIER THAT ACTUALLY FAILED. These strings
 * used to hardcode "SanMar", which is how a distributor on S&S read
 * "Unknown SanMar SKU. Please pick a SKU from the SanMar catalog." about a
 * supplier they had not selected -- copy that is not merely wrong but
 * actively misdirecting, since it sends them to the wrong catalog.
 *
 * ``supplierName`` is the SERVER's display name for the selected supplier,
 * so the copy says whatever the picker said. It falls back to a neutral
 * noun rather than to "SanMar": with no name resolved, a vague sentence is
 * honest and a specific one is a guess.
 */
function mapError(status, supplierName = null) {
    const who = supplierName || 'this supplier'
    if (status === 404) {
        return {
            status: 404,
            message: `Unknown ${who} SKU. Please pick a SKU from the ${who} catalog.`,
        }
    }
    if (status === 500) {
        return {
            status: 500,
            message:
                `Could not process this product\u2019s ${who} data. This is not a `
                + 'connectivity issue \u2014 retrying will not help. Please contact '
                + 'support.',
        }
    }
    if (status === 503) {
        // supplier_not_configured: no account is set up for this supplier, so
        // "try again" would be false advice (GH #4424).
        return {
            status: 503,
            message:
                `${who} is not set up for this account yet. Connect it under `
                + 'Suppliers, or contact support.',
        }
    }
    if (status === 502) {
        return {
            status: 502,
            message: `${who} feed unavailable \u2014 try again in a minute.`,
        }
    }
    return {
        status: 0,
        message: 'Failed to load product information.',
    }
}
